import { type FC } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
  Badge,
} from "flowbite-react";

interface ContractType {
  id: number;
  type: string;
  company_details?: {
    company: string;
    color_code: string;
  };
}

interface ContractConfirmationModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  contractData: {
    contract: {
      firstName: string;
      birthDate: string;
      email: string;
      phone: string;
      bank_details: string;
    };
    name: string;
    floor: string;
    comments: string;
    selectedContractTypes: ContractType[];
  };
  address: {
    street: string;
    house_number: string;
    zip_code: string;
    city: string;
  } | null;
}

const ContractConfirmationModal: FC<ContractConfirmationModalProps> = ({
  show,
  onClose,
  onConfirm,
  isLoading,
  contractData,
  address,
}) => {
  const birthDate = new Date(contractData.contract.birthDate);
  const birthDateDisplay = Number.isFinite(birthDate.getTime())
    ? birthDate.toLocaleDateString()
    : "-";

  return (
    <Modal show={show} onClose={onClose} size="xl">
      <ModalHeader className="border-b border-gray-200 !p-6 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Details bestätigen
        </h3>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-6">
          {/* Personal Information */}
          <div>
            <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-white">
              Persönliche Informationen
            </h4>
            <div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Vollständiger Name
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contractData.contract.firstName} {contractData.name}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Geburtsdatum
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {birthDateDisplay}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  E-Mail
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contractData.contract.email}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Telefon
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contractData.contract.phone}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Bankverbindung
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {contractData.contract.bank_details
                    ? "•".repeat(
                        Math.min(
                          (contractData.contract.bank_details || "").length,
                          16
                        )
                      )
                    : "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Address Information */}
          {address && (
            <div>
              <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-white">
                Adressinformationen
              </h4>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Straße & Hausnummer
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {address.street} {address.house_number}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Etage
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {contractData.floor}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      PLZ & Stadt
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {address.zip_code} {address.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contract Information */}
          {contractData.selectedContractTypes.length > 0 && (
            <div>
              <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-white">
                Informationen ({contractData.selectedContractTypes.length}{" "}
                Produkte)
              </h4>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-2 flex flex-wrap gap-2">
                  {contractData.selectedContractTypes.map((contractType) => (
                    <Badge
                      key={contractType.id}
                      color="info"
                      className="flex items-center gap-2 px-3 py-1.5"
                      style={{
                        backgroundColor:
                          contractType.company_details?.color_code ?? "#6B7280",
                      }}
                    >
                      <span>
                        {contractType.type || "Unbekannt"} -{" "}
                        {contractType.company_details?.company ?? "Unbekannt"}
                      </span>
                    </Badge>
                  ))}
                </div>
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  Alle ausgewählten Produkte werden für diesen Kunden erstellt.
                </p>
              </div>
            </div>
          )}

          {/* Additional Comments */}
          {contractData.comments && (
            <div>
              <h4 className="mb-3 text-base font-medium text-gray-900 dark:text-white">
                Zusätzliche Kommentare
              </h4>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-sm text-gray-900 dark:text-white">
                  {contractData.comments}
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex w-full justify-end gap-4">
          <Button color="gray" onClick={onClose}>
            Abbrechen
          </Button>
          <Button color="success" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "Wird gespeichert..." : "Bestätigen & Speichern"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ContractConfirmationModal;
